import { Component, OnInit, Injector } from '@angular/core';
import { BaseComponent } from '../base/base.component';
import { Printer, Store } from '../../models/RestModels';
import { PrinterService, LocalPrinterPreferences, PrintResult } from '../../services/printer.service';
import { forkJoin } from 'rxjs';

interface PrinterSelection {
	printer: Printer;
	selected: boolean;
}

@Component({
	selector: 'app-select-printer-config',
	templateUrl: './select-printer-config.component.html',
	styleUrls: ['./select-printer-config.component.css']
})
export class SelectPrinterConfigComponent extends BaseComponent implements OnInit {

	// Available printers for the store
	printer_list: Printer[] = [];
	store_list: Store[] = [];
	store_map: Map<number, Store> = new Map();

	// Current preferences
	preferences: LocalPrinterPreferences = {
		print_method: 'BROWSER',
		pos_printer_id: null,
		kitchen_printer_ids: []
	};

	// Kitchen printer selections
	kitchen_selections: PrinterSelection[] = [];

	// Connection test status
	test_status: Map<number, 'idle' | 'testing' | 'success' | 'error'> = new Map();
	test_messages: Map<number, string> = new Map();

	private printerService: PrinterService;

	constructor(public injector: Injector) {
		super(injector);
		this.printerService = injector.get(PrinterService);
	}

	ngOnInit() {
		this.setTitle('Seleccionar Impresora');
		this.is_loading = true;

		// Load printers and current preferences
		this.subs.sink = forkJoin({
			printers: this.rest.printer.search({
				limit: 9999,
				eq: { store_id: this.rest.current_user?.store_id },
				sort_order: ['name_ASC']
			}),
			stores: this.rest.store.search({ limit: 9999, sort_order: ['name_ASC'] })
		}).subscribe(
			(responses) => {
				this.printer_list = responses.printers.data;
				this.store_list = responses.stores.data;

				// Build store map
				this.store_map.clear();
				for (const store of this.store_list) {
					if (store.id) {
						this.store_map.set(store.id, store);
					}
				}

				// Load current preferences
				this.preferences = this.printerService.loadLocalPreferences();

				// Initialize kitchen selections
				this.kitchen_selections = this.printer_list.map(printer => ({
					printer,
					selected: this.preferences.kitchen_printer_ids.includes(printer.id)
				}));

				// Initialize test status
				for (const printer of this.printer_list) {
					this.test_status.set(printer.id, 'idle');
					this.test_messages.set(printer.id, '');
				}

				this.is_loading = false;
			},
			(error) => {
				this.showError(error);
				this.is_loading = false;
			}
		);
	}

	getStoreName(store_id: number): string {
		const store = this.store_map.get(store_id);
		return store?.name || 'Sin sucursal';
	}

	/**
	 * Handle print method change
	 */
	onPrintMethodChange(): void {
		// If switching to USB and no printer selected, try to auto-select if only one exists
		if (this.preferences.print_method === 'USB' && !this.preferences.pos_printer_id && this.printer_list.length === 1) {
			this.preferences.pos_printer_id = this.printer_list[0].id;
		}
		this.savePreferences();
	}

	/**
	 * Handle POS printer selection change
	 */
	onPosPrinterChange(): void {
		this.savePreferences();
	}

	/**
	 * Handle kitchen printer selection change
	 */
	onKitchenPrinterChange(selection: PrinterSelection): void {
		this.preferences.kitchen_printer_ids = this.kitchen_selections
			.filter(s => s.selected)
			.map(s => s.printer.id);
		this.savePreferences();
	}

	/**
	 * Save preferences to localStorage
	 */
	savePreferences(): void {
		this.printerService.saveLocalPreferences(this.preferences);
		this.showSuccess('Preferencias guardadas');
	}

	/**
	 * Test connection to a specific printer
	 */
	async testConnection(printer: Printer, event: Event): Promise<void> {
		console.log('Testing printer:', printer);
		console.log('Vendor ID:', printer.vendor_id, 'Product ID:', printer.product_id);

		this.test_status.set(printer.id, 'testing');
		this.test_messages.set(printer.id, 'Conectando...');

		try {
			const result: PrintResult = await this.printerService.testConnection(printer, event);

			if (result.success) {
				this.test_status.set(printer.id, 'success');
				this.test_messages.set(printer.id, result.message);
				this.showSuccess(`${printer.name}: ${result.message}`);
			} else {
				this.test_status.set(printer.id, 'error');
				this.test_messages.set(printer.id, result.message + (result.error ? `: ${result.error}` : ''));
				this.showError(`${printer.name}: ${result.message}`);
			}
		} catch (error: any) {
			this.test_status.set(printer.id, 'error');
			this.test_messages.set(printer.id, error?.message || 'Error desconocido');
			this.showError(`${printer.name}: ${error?.message || 'Error desconocido'}`);
		}
	}

	/**
	 * Get printer name by ID
	 */
	getPrinterName(printerId: number | null): string {
		if (!printerId) return 'Ninguna';
		const printer = this.printer_list.find(p => p.id === printerId);
		return printer?.name || 'No encontrada';
	}

	/**
	 * Get CSS class for test status
	 */
	getTestStatusClass(printerId: number): string {
		const status = this.test_status.get(printerId);
		switch (status) {
			case 'testing': return 'text-warning';
			case 'success': return 'text-success';
			case 'error': return 'text-danger';
			default: return 'text-muted';
		}
	}

	/**
	 * Get icon for test status
	 */
	getTestStatusIcon(printerId: number): string {
		const status = this.test_status.get(printerId);
		switch (status) {
			case 'testing': return 'bi-hourglass-split';
			case 'success': return 'bi-check-circle-fill';
			case 'error': return 'bi-x-circle-fill';
			default: return 'bi-question-circle';
		}
	}
}
